import styled from "@emotion/styled";
import { Typography, useTheme } from "@mui/material";
import { capitalize } from "lodash";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectWrapper = styled.select`
  font-family: inherit;
  font-size: inherit;
  padding: 8px 16px;
  outline: none;
  border: ${props => (props.bg ? "none" : "2px solid #3cadb7")};
  border-radius: 8px;
  background: ${props =>
    props.bg
      ? props.neutral
        ? props.theme?.palette?.neutral?.main || "#DFF5F5"
        : "#DFF5F5"
      : "transparent"};
  color: ${props =>
    props.bg
      ? props.neutral
        ? props.theme?.palette?.text?.primary || "#3cadb7"
        : "#3cadb7"
      : "#000000"};

  :disabled {
    cursor: not-allowed;
    background: ${props =>
      props.bg
        ? props.neutral
          ? props.theme?.palette?.neutral?.light || "#f5f5f5"
          : props.theme?.palette?.secondary?.light || "#e0e0e0"
        : props.theme?.palette?.grey?.[100] || "#f5f5f5"}
    color: ${props => props.theme?.palette?.text?.disabled || "#9e9e9e"};
    border-color: ${props => props.theme?.palette?.grey?.[300] || "#d1d5db"};
    opacity: 0.6;
  }
`;

const HelperText = styled.span`
  padding: 0 0.75rem;
  font-size: 0.75rem;
  color: ${props =>
    props.error ? props.theme.palette.error.main : "darkgray"};
`;

export const Select = ({
  label,
  name,
  isLabelBold,
  isLabelEmpty,
  options,
  error,
  helperText,
  register,
  hasNoBg = false,
  neutral,
  required,
  placeholder,
  value,
  defaultValue,
  ...rest
}) => {
  const theme = useTheme();

  // A consumer that passes `value` is controlled; otherwise the placeholder
  // option is selected by default (uncontrolled). Passing both `value` and
  // `defaultValue` triggers React's controlled/uncontrolled warning.
  const valueProps =
    value !== undefined ? { value } : { defaultValue: defaultValue ?? "" };

  return (
    <Container>
      {!isLabelEmpty && (
        <Typography
          variant="subtitle2"
          component="label"
          htmlFor={name}
          sx={{
            fontWeight: isLabelBold && 700,
            "&:after": {
              content: required ? '" *"' : '""',
              color: "red"
            }
          }}
        >
          {label}
        </Typography>
      )}
      <SelectWrapper
        {...valueProps}
        {...register(name)}
        bg={neutral || !hasNoBg}
        neutral={neutral}
        theme={theme}
        {...rest}
      >
        <option value="" disabled hidden={!placeholder}>
          {placeholder}
        </option>
        {options.map(({ optionLabel, value, ...rest }) => (
          <option key={value} value={value} {...rest}>
            {optionLabel}
          </option>
        ))}
      </SelectWrapper>
      {!!helperText && (
        <HelperText error={error}>{capitalize(helperText)}</HelperText>
      )}
    </Container>
  );
};
